import { Link } from "@mui/material";

import { routes } from "@/global/routes";
import {
  DefaultCheckoutTerms,
  DefaultRegistrationTerms,
} from "../../default_terms_and_conditions";
import { faqs, howItWorks, AnalyticsHeader } from "./helpers";
import { inventoryTypeUIDs, vendorUUIDs } from "../../constants";

const LogoImage =
  "https://ss-leagues.s3.amazonaws.com/Minor%20League%20Baseball/Northwest%20Arkansas%20Naturals/arkansas-naturals-logo.webp";
const FindGamesBanner =
  "https://ss-leagues.s3.amazonaws.com/Minor%20League%20Baseball/Northwest%20Arkansas%20Naturals/arkansas-naturals-banner.png";
const BackgroundImage =
  "https://ss-leagues.s3.amazonaws.com/Minor%20League%20Baseball/Northwest%20Arkansas%20Naturals/arkansas-naturals-background.png";
const StaticSeatMap =
  "https://ss-leagues.s3.amazonaws.com/Minor%20League%20Baseball/Northwest%20Arkansas%20Naturals/Ballpark_map.jpg";

import type { TeamConfig } from "../types";

const config: TeamConfig = {
  teamSchema: "northwestarkansasnaturals_flex",
  browserTitle: "Naturals Flex",
  packages: false,
  defaultRoute: routes.purchaseSeats,
  phoneNumber: "",
  teamName: "Northwest Arkansas Naturals",

  packs: {
    enabled: true,
    list: [
      {
        id: 1,
        amount: 3,
        offerMoreMessage:
          "Switch to a 4 Game Pack and get 20% off your tickets!",
        title: "3 Game Pack",
        description: "Pick any 3 games, get up to 20% off per game.",
        scrollToTopWhenSelecting: true,
        buckets: [
          {
            id: 1,
            max: 3,
            min: 3,
            description: "",
            title: "Upcoming Games",
          },
        ],
        successMessage:
          "You're good to go! Add more games or select Continue to view seating options.",
        updatedAccountGroup: 0,
        customProgressBarLanguage: [
          {
            selectedEventCount: 0,
            progressBarText: "Add 3 more games to complete your pack.",
          },
          {
            selectedEventCount: 1,
            progressBarText: "Add 2 more games to complete your pack.",
          },
          {
            selectedEventCount: 2,
            progressBarText: "Add 1 more game to complete your pack.",
          },
          {
            selectedEventCount: 3,
            progressBarText:
              "You're good to go! Click Continue to view seating options.",
          },
        ],
      },
      {
        id: 2,
        amount: 4,
        title: "4 Game Pack",
        defaultSelected: true,
        offerMoreMessage:
          "Switch to a 5 Game Pack and get 25% off your tickets!",
        description:
          "Pick any 4 games, get up to 20% off per game and unlock July 4th access",
        scrollToTopWhenSelecting: true,
        buckets: [
          {
            id: 1,
            max: 4,
            min: 4,
            description: "",
            title: "Upcoming Games",
          },
        ],
        successMessage:
          "You're good to go! Add more games or select Continue to view seating options.",
        updatedAccountGroup: 1,
        customProgressBarLanguage: [
          {
            selectedEventCount: 0,
            progressBarText: "Add 4 more games to complete your pack.",
          },
          {
            selectedEventCount: 1,
            progressBarText: "Add 3 more games to complete your pack.",
          },
          {
            selectedEventCount: 2,
            progressBarText: "Add 2 more games to complete your pack.",
          },
          {
            selectedEventCount: 3,
            progressBarText: "Add 1 more game to complete your pack.",
          },
          {
            selectedEventCount: 4,
            progressBarText:
              "You're good to go! Click Continue to view seating options.",
          },
        ],
      },
      {
        id: 3,
        amount: 5,
        title: "5 Game Pack",
        offerMoreMessage: "",
        description:
          "Pick any 5 games, get up to 25% off per game and unlock July 4th access.",
        scrollToTopWhenSelecting: true,
        buckets: [
          {
            id: 1,
            max: 5,
            min: 5,
            description: "",
            title: "Upcoming Games",
          },
        ],
        successMessage:
          "You're good to go! Add more games or select Continue to view seating options.",
        updatedAccountGroup: 2,
        customProgressBarLanguage: [
          {
            selectedEventCount: 0,
            progressBarText: "Add 5 more games to complete your pack.",
          },
          {
            selectedEventCount: 1,
            progressBarText: "Add 4 more games to complete your pack.",
          },
          {
            selectedEventCount: 2,
            progressBarText: "Add 3 more games to complete your pack.",
          },
          {
            selectedEventCount: 3,
            progressBarText: "Add 2 more games to complete your pack.",
          },
          {
            selectedEventCount: 4,
            progressBarText: "Add 1 more game to complete your pack.",
          },
          {
            selectedEventCount: 5,
            progressBarText:
              "You're good to go! Click Continue to view seating options.",
          },
        ],
      },
    ],
  },

  // seat map configs
  seatMapConfigs: {
    mapId: "",
    venueId: "",
    isView3dButtonHidden: true,
    staticSeatMapImage: StaticSeatMap,
    isShowAvailableSeatsButtonHidden: true,
    customSeatMapStaticImageStyle: {
      display: "block",
      marginX: "auto",
      width: "70%",
    },
  },

  // gift cards
  giftCardConfigs: {
    isMenuGiftCardEnabled: false,
    isLandingPageGiftCardEnabled: false,
    isStandalonePageGiftCardEnabled: false,
    redirectGiftCardUrl: routes.redeem,
  },

  // purchase credit configs
  purchaseCreditConfigs: {
    isLandingPurchaseCreditEnabled: false,
    isStandAlonePurchaseCreditEnabled: false,
    showPurchaseCreditLinkInHomeMenu: false,
    isPurchaseCreditHistoryEnabled: false,
  },

  // disabled routes
  disabledRoutes: [routes.giftCard],

  // switches
  checkoutCreditPackages: false,
  availableCreditEnabled: false,
  isCrowdHandlerEnabled: true,
  inventoryType: inventoryTypeUIDs.seatLevel,

  // purchase seats config
  purchaseSeatConfigs: {
    minGameSelectionCount: 3,
    maxGameSelectionCount: 5,
    minSeatLimit: 1,
    isSimilarSeatForAllEvents: true,
    allowManualCartInSimilar: true,
    isCheckoutEditSeatsEnabled: true,
    isDiffSeatAlertEnabled: true,
    defaultSeatCount: 2,
    checkoutTimerEnabled: false,
  },

  // feature configs
  packageConfigs: {
    isPackageEnabled: false,
    canRemovePackageGames: false,
    staticBuildYourOwn: null,
  },

  // intercom settings
  intercomConfigs: {
    isIntercomEnabled: true,
    allowedPages: [],
  },

  // checkout validations
  checkoutValidation: {
    allowedZipFailCount: Infinity,
    allowedPaymentFailCount: Infinity,
  },

  // analytics configs
  analyticsConfigs: {
    googleAnalyticsId: "",
    attrkPixelId: "",
    beaconId: "",
    AnalyticsHeader,
  },

  // redirect configs
  redirectConfigs: {
    internalRedirectUrl:
      "https://flex.attendevents.com/northwestarkansasnaturals_flex",
    externalRedirectUrl: "",
  },

  refreshInventoryWhenEventClick: false,

  isSaveCardAllowed: true,

  // urls
  accountManagerUrl:
    "https://www.milb.com/northwest-arkansas/tickets/my-naturals-tickets",

  // vendor
  platform: vendorUUIDs.tickesdotcom,

  // okta config
  oktaIssuer: "https://ids.mlb.com/oauth2/aus1m088yK07noBfh356",
  oktaClientId: "0oasmcdgh92Jom7TI357",

  isBrainTreePaymentEnabled: false,

  // images
  images: {
    logoNameImage: "",
    faviconImage: LogoImage,
    seatMap3dPreviewImage: "",
    authBackgroundImage: BackgroundImage,
    findGamesBannerImage: FindGamesBanner,
    checkoutStatusPageImage: BackgroundImage,
    logoImage: LogoImage + "?width=128&height=66",
  },

  uiConfigs: {
    bannerImage: {
      title: "",
      isBackgroundBasedBanner: false,
    },

    featuredGameCard: {
      hideOverlayLogo: true,
      showLogoWithTitle: false,
    },

    bottomBar: {
      showGameSelectionProgress: true,
    },

    gameFiltersSettings: {
      hideAccessibilityFilter: false,
      hideSectionFilter: false,
    },

    shouldDisplayServiceFee: false,
    shouldAddServiceFeeWithPerkPrice: true,

    customAdaLabel: (
      <>
        To purchase accessible seating locations, please contact the Box Office
        at{" "}
        <Link
          href="tel:4799274900"
          sx={{
            fontWeight: 500,
            color: "primary.main",
            textDecoration: "none",
          }}
        >
          (479) 927-4900
        </Link>
        .
      </>
    ),
  },

  colors: {
    primary: "#00539f",
  },

  howItWorks: howItWorks,
  faqs: faqs,

  languages: {
    topNavMenuItems: null,
    checkoutTermsAndCondition: <DefaultCheckoutTerms />,
    registrationTermsAndCondition: <DefaultRegistrationTerms />,

    purchaseSeats: {
      similarSeatModalBody: (seatTitle: string) => (
        <>
          We are going to apply the same or similar seats to all games in your
          pack. Seat locations will be visible on the checkout page.
        </>
      ),
    },

    purchaseGiftCard: null,
    redeemGiftCard: null,

    checkoutSuccessMessage: "Your purchase is complete!",
    checkoutSuccessDescription: (
      <>
        Your Northwest Arkansas Naturals tickets are confirmed! Your tickets
        will be available within 24 hours in your{" "}
        <Link
          target="_blank"
          href="https://www.milb.com/northwest-arkansas/tickets/my-naturals-tickets"
          sx={{
            fontWeight: 500,
            color: "primary.main",
            textDecoration: "none",
          }}
        >
          Account Manager
        </Link>{" "}
        account.
      </>
    ),

    giftCard: {
      title: "",
      createGiftCard: {
        description: "",
        recipient: {
          sendNowFalse: {
            description: "",
          },
          sendNowTrue: {
            description: "",
          },
        },
      },
      redeemGiftCard: {
        loggedIn: {
          description: "",
        },
        logout: {
          description: "",
        },
        success: {
          description: "",
        },
      },
    },

    signInAuthText:
      "Connect your mlb.com account to continue with seamless access.",
  },
};

export default config;
